import { useState, useEffect, useMemo, useRef } from "react";
import { DotsVertical, Edit05, Trash01, CheckCircle, AlertCircle, Paperclip } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { Avatar } from "@/components/base/avatar/avatar";
import { documentReviewService } from "@/data/document-review-polygons";
import type { ReviewPolygon } from "@/types/document-review";
import { cx } from "@/utils/cx";

type AnnotationDrawerProps = {
    isOpen: boolean;
    polygon: ReviewPolygon | null;
    allPolygons: ReviewPolygon[];
    documentId: string;
    onClose: () => void;
    onUpdate: (updates: Partial<ReviewPolygon>) => void;
    onSelectPolygon: (polygonId: string) => void;
    onMarkResolved: (polygonId: string, resolved: boolean) => void;
    onDelete: (polygonId: string) => void;
};

export function AnnotationDrawer({
    polygon,
    allPolygons,
    documentId,
    onUpdate,
    onSelectPolygon,
    onMarkResolved,
    onDelete
}: AnnotationDrawerProps) {
    const [activeTab, setActiveTab] = useState<'details' | 'chat' | 'all'>('all');
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [attributes, setAttributes] = useState<Record<string, string>>({});
    const [chatMessage, setChatMessage] = useState("");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [actionPolygonId, setActionPolygonId] = useState<string | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'resolved'>('all');
    const [searchQuery, setSearchQuery] = useState("");
    const chatListRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (polygon) {
            setName(polygon.name || "");
            setNotes(polygon.notes || "");
            setAttributes(polygon.attributes || {});
        } else {
            setName("");
            setNotes("");
            setAttributes({});
        }
    }, [polygon]);

    const filteredPolygons = useMemo(() => {
        return allPolygons.filter((p) => {
            // Filtro per stato
            const isResolved = p.attributes?.resolved === 'true';
            if (statusFilter === 'pending' && isResolved) return false;
            if (statusFilter === 'resolved' && !isResolved) return false;

            // Filtro per ricerca
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const searchText = [
                    p.name?.toLowerCase(),
                    p.notes?.toLowerCase(),
                    `pagina ${p.pageNumber}`,
                ].filter(Boolean).join(' ');

                if (!searchText.includes(query)) return false;
            }

            return true;
        });
    }, [allPolygons, statusFilter, searchQuery]);

    const handleSave = () => {
        if (!polygon) return;
        onUpdate({ name, notes, attributes });
    };

    const handleAddAttribute = () => {
        setAttributes((prev) => ({ ...prev, [`new_attribute_${Date.now()}`]: "" }));
    };

    const handleAttributeChange = (key: string, value: string) => {
        setAttributes((prev) => ({ ...prev, [key]: value }));
    };

    const handleRemoveAttribute = (keyToRemove: string) => {
        setAttributes((prev) => {
            const newAttributes = { ...prev };
            delete newAttributes[keyToRemove];
            return newAttributes;
        });
    };

    const handleAddComment = () => {
        if (!polygon || !chatMessage.trim()) return;
        // Mock user for now - in futuro prelevare dall'utente corrente
        const userId = "mockUser1";
        const userName = "John Doe"; // Mock user name
        const updatedPolygon = documentReviewService.addCommentToPolygon(
            documentId,
            polygon.id,
            userId,
            userName,
            chatMessage
        );
        if (updatedPolygon) {
            setChatMessage("");
            // Refresh polygon data
            onUpdate({});
            // Auto-scroll to bottom after adding comment
            setTimeout(() => {
                chatListRef.current?.scrollTo({ top: chatListRef.current.scrollHeight, behavior: "smooth" });
            }, 100);
        }
    };

    // Auto-scroll chat when comments change
    useEffect(() => {
        if (activeTab === 'chat' && polygon?.comments) {
            chatListRef.current?.scrollTo({ top: chatListRef.current.scrollHeight, behavior: "smooth" });
        }
    }, [polygon?.comments?.length, activeTab]);

    return (
        <div className="fixed right-0 top-16 lg:top-18 bottom-0 w-[380px] bg-primary shadow-lg ring-1 ring-secondary z-50 flex flex-col">
            <div className="p-4 border-b border-secondary">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-md font-semibold text-primary">Annotazioni</div>
                </div>

                {/* TABS */}
                <div className="flex gap-2 border-b border-secondary">
                    <button
                        onClick={() => setActiveTab('all')}
                        className={`px-3 py-2 text-sm ${activeTab === 'all' ? 'border-b-2 border-brand-600 text-brand-600' : 'text-tertiary'}`}
                    >
                        Tutti ({allPolygons.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('details')}
                        className={`px-3 py-2 text-sm ${activeTab === 'details' ? 'border-b-2 border-brand-600 text-brand-600' : 'text-tertiary'}`}
                    >
                        Dettagli
                    </button>
                    <button
                        onClick={() => setActiveTab('chat')}
                        className={`px-3 py-2 text-sm ${activeTab === 'chat' ? 'border-b-2 border-brand-600 text-brand-600' : 'text-tertiary'}`}
                    >
                        Chat
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
                {/* TAB: Details */}
                {activeTab === 'details' && polygon && (
                    <div className="space-y-6">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-primary mb-1">
                                Nome Segnalazione
                            </label>
                            <Input
                                id="name"
                                placeholder="Es: Errore su importo"
                                value={name}
                                onChange={(value) => setName(value as string)}
                            />
                        </div>
                        <div>
                            <label htmlFor="notes" className="block text-sm font-medium text-primary mb-1">
                                Note
                            </label>
                            <textarea
                                id="notes"
                                className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary shadow-xs-skeumorphic focus:border-brand-solid focus:ring-brand-solid"
                                rows={4}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>

                        <div>
                            <h3 className="text-md font-medium text-primary mb-2">Attributi</h3>
                            {Object.entries(attributes).map(([key, value]) => (
                                <div key={key} className="flex items-center gap-2 mb-2">
                                    <Input
                                        label=""
                                        placeholder="Chiave"
                                        value={key}
                                        onChange={(newKey) => {
                                            const newKeyStr = newKey as string;
                                            setAttributes((prev) => {
                                                const newAttrs = { ...prev };
                                                delete newAttrs[key];
                                                return { ...newAttrs, [newKeyStr]: value };
                                            });
                                        }}
                                        className="flex-1"
                                    />
                                    <Input
                                        label=""
                                        placeholder="Valore"
                                        value={value}
                                        onChange={(newValue) => handleAttributeChange(key, newValue as string)}
                                        className="flex-1"
                                    />
                                    <ButtonUtility
                                        size="sm"
                                        color="secondary"
                                        icon={Trash01}
                                        onClick={() => handleRemoveAttribute(key)}
                                        tooltip="Rimuovi attributo"
                                        className="flex-shrink-0"
                                    />
                                </div>
                            ))}
                            <Button size="sm" color="secondary" onClick={handleAddAttribute} className="mt-2">
                                Aggiungi Attributo
                            </Button>
                        </div>

                        <div className="pt-4 border-t border-secondary">
                            <Button size="md" color="primary" onClick={handleSave} className="w-full">
                                Salva Modifiche
                            </Button>
                        </div>
                    </div>
                )}

                {/* TAB: Chat */}
                {activeTab === 'chat' && polygon && (
                    <div className="flex h-full min-h-0 flex-col">
                        <div ref={chatListRef} className="flex-1 overflow-y-auto py-4">
                            <div className="flex flex-col gap-5">
                                {polygon.comments?.map((comment) => {
                                    const timestamp = comment.timestamp || comment.createdAt;
                                    const userName = comment.userName || comment.author || "Utente";
                                    const formatTimestamp = (ts?: string) => {
                                        if (!ts) return undefined;
                                        try {
                                            return new Date(ts).toLocaleString('it-IT', {
                                                day: 'numeric',
                                                month: 'short',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                            });
                                        } catch {
                                            return undefined;
                                        }
                                    };

                                    return (
                                        <div key={comment.id} className="flex items-start">
                                            <Avatar size="sm" src="https://www.untitledui.com/images/avatars/ethan-valdez?bg=%23E0E0E0" status="online" className="mr-3" />
                                            <div className="flex-1">
                                                <div className="mb-1 flex items-baseline justify-between pr-1">
                                                    <span className="text-sm font-medium text-primary">{userName}</span>
                                                    {timestamp && <span className="text-xs text-tertiary">{formatTimestamp(timestamp)}</span>}
                                                </div>
                                                <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                                                    {comment.text}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!polygon.comments || polygon.comments.length === 0) && (
                                    <div className="text-center text-tertiary py-8">
                                        <div className="text-sm">Nessun commento ancora</div>
                                        <div className="text-xs mt-1">Inizia la conversazione</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleAddComment();
                            }}
                            className="border-t border-secondary py-3"
                        >
                            <div
                                className="relative w-full rounded-lg bg-primary ring-1 ring-primary transition-shadow duration-100 ease-linear focus-within:ring-2 focus-within:ring-brand"
                            >
                                {/* Top-right subtle recording icon */}
                                <div className="pointer-events-none absolute right-3 top-3 flex gap-1 opacity-50">
                                    <Paperclip className="size-4 dark:text-white" />
                                </div>

                                <TextArea
                                    value={chatMessage}
                                    onChange={(val) => setChatMessage(val as string)}
                                    rows={4}
                                    placeholder="Scrivi un messaggio..."
                                    aria-label="Scrivi un messaggio nella chat"
                                    textAreaClassName={(state) =>
                                        cx(
                                            "min-h-[88px] resize-none border-0 bg-transparent px-3.5 pb-10 pt-3 shadow-none ring-0 outline-hidden",
                                            typeof state === "string" && state,
                                        )
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleAddComment();
                                        }
                                    }}
                                    className=""
                                />

                                {/* Bottom-right actions */}
                                <div className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover"
                                        aria-label="Allega"
                                    >
                                        <Paperclip className="size-4" />
                                    </button>
                                    <button
                                        type="button"
                                        className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover"
                                        aria-label="Emoji"
                                    >
                                        <span className="block text-base leading-none">☺︎</span>
                                    </button>
                                    <Button className="pointer-events-auto" color="link-color" size="md" type="submit">
                                        Invia
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* TAB: All Issues */}
                {activeTab === 'all' && (
                    <div className="space-y-3">
                        {/* Filtri e ricerca */}
                        <div className="space-y-2">
                            {/* Filtro stato */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setStatusFilter('all')}
                                    className={`px-3 py-1.5 rounded text-sm ${statusFilter === 'all' ? 'bg-brand-solid dark:text-white' : 'bg-secondary text-tertiary hover:bg-secondary_hover'}`}
                                >
                                    Tutti
                                </button>
                                <button
                                    onClick={() => setStatusFilter('pending')}
                                    className={`px-3 py-1.5 rounded text-sm ${statusFilter === 'pending' ? 'bg-brand-solid dark:text-white' : 'bg-secondary text-tertiary hover:bg-secondary_hover'}`}
                                >
                                    Da risolvere
                                </button>
                                <button
                                    onClick={() => setStatusFilter('resolved')}
                                    className={`px-3 py-1.5 rounded text-sm ${statusFilter === 'resolved' ? 'bg-brand-solid dark:text-white' : 'bg-secondary text-tertiary hover:bg-secondary_hover'}`}
                                >
                                    Risolto
                                </button>
                            </div>

                            {/* Campo ricerca */}
                            <Input
                                placeholder="Cerca per nome, note o pagina..."
                                value={searchQuery}
                                onChange={(value) => setSearchQuery(value as string)}
                            />
                        </div>

                        {/* Lista filtrata */}
                        <div className="space-y-2">
                            {allPolygons.length === 0 ? (
                                <div className="p-8 text-center text-tertiary">
                                    <div className="text-sm">Nessun rettangolo creato</div>
                                    <div className="text-xs mt-1">Clicca su "+ Aggiungi Rettangolo" per iniziare</div>
                                </div>
                            ) : filteredPolygons.length === 0 ? (
                                <div className="p-8 text-center text-tertiary">
                                    <div className="text-sm">Nessuna segnalazione trovata</div>
                                    <div className="text-xs mt-1">Prova a modificare i filtri o la ricerca</div>
                                </div>
                            ) : (
                                filteredPolygons.map((p) => (
                                    <div
                                        key={p.id}
                                        className={`p-3 rounded border cursor-pointer ${p.id === polygon?.id ? 'border-brand-600' : 'border-secondary hover:border-brand-300'} bg-primary`}
                                        onClick={() => onSelectPolygon(p.id)}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1">
                                                <div className="text-sm font-medium text-primary">
                                                    {p.name || `Segnalazione #${p.id.substring(0, 8)}`}
                                                </div>
                                                <div className="text-xs text-tertiary mt-1">
                                                    Pagina {p.pageNumber}
                                                    {!p.name && p.notes && ` • ${p.notes}`}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {/* Status icon */}
                                                {p.attributes?.resolved === 'true' ? (
                                                    <CheckCircle className="size-5 text-success-600" />
                                                ) : (
                                                    <AlertCircle className="size-5 text-warning-600" />
                                                )}

                                                {/* Dropdown menu */}
                                                <div className="relative">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDropdownOpen(dropdownOpen === p.id ? null : p.id);
                                                        }}
                                                        className="p-1 hover:bg-secondary rounded"
                                                    >
                                                        <DotsVertical className="size-4 text-tertiary" />
                                                    </button>

                                                    {dropdownOpen === p.id && (
                                                        <div className="absolute right-0 mt-1 w-48 bg-primary border border-secondary rounded-lg shadow-lg z-10">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onSelectPolygon(p.id);
                                                                    setActiveTab('details');
                                                                    setDropdownOpen(null);
                                                                }}
                                                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-primary hover:bg-secondary"
                                                            >
                                                                <Edit05 className="size-4" />
                                                                Modifica
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActionPolygonId(p.id);
                                                                    setResolveModalOpen(true);
                                                                    setDropdownOpen(null);
                                                                }}
                                                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-primary hover:bg-secondary"
                                                            >
                                                                <CheckCircle className="size-4" />
                                                                {p.attributes?.resolved === 'true' ? 'Segna come da risolvere' : 'Segna come risolto'}
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActionPolygonId(p.id);
                                                                    setDeleteModalOpen(true);
                                                                    setDropdownOpen(null);
                                                                }}
                                                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-secondary"
                                                            >
                                                                <Trash01 className="size-4" />
                                                                Elimina
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {!polygon && activeTab !== 'all' && (
                    <div className="p-4 text-tertiary">Seleziona un rettangolo per visualizzare le annotazioni.</div>
                )}
            </div>

            {/* Modale Delete */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                    <div className="bg-primary rounded-lg p-6 max-w-md">
                        <h3 className="text-lg font-semibold text-primary mb-2">Elimina Segnalazione</h3>
                        <p className="text-sm text-tertiary mb-4">
                            Sei sicuro di voler eliminare questa segnalazione? Questa azione non può essere annullata.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
                                Annulla
                            </Button>
                            <Button
                                color="primary"
                                onClick={() => {
                                    if (actionPolygonId) {
                                        onDelete(actionPolygonId);
                                    }
                                    setDeleteModalOpen(false);
                                    setActionPolygonId(null);
                                }}
                            >
                                Elimina
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale Resolve */}
            {resolveModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                    <div className="bg-primary rounded-lg p-6 max-w-md">
                        <h3 className="text-lg font-semibold text-primary mb-2">Cambia Stato</h3>
                        <p className="text-sm text-tertiary mb-4">
                            Confermi di voler cambiare lo stato di questa segnalazione?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button color="secondary" onClick={() => setResolveModalOpen(false)}>
                                Annulla
                            </Button>
                            <Button
                                color="primary"
                                onClick={() => {
                                    if (actionPolygonId) {
                                        const p = allPolygons.find(poly => poly.id === actionPolygonId);
                                        if (p) {
                                            onMarkResolved(actionPolygonId, !(p.attributes?.resolved === 'true'));
                                        }
                                    }
                                    setResolveModalOpen(false);
                                    setActionPolygonId(null);
                                }}
                            >
                                Conferma
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}